// components/FormField.tsx

import React, { useState, useEffect, useRef } from 'react';
import { FormGroup, Label, Input, Spinner } from 'reactstrap';
import Select from 'react-select';
import { FieldInputProps, FormikTouched, FormikErrors } from 'formik';
import { InputType } from 'reactstrap/types/lib/Input';
import { useDebounce } from 'hooks/useDebounce';

interface Option {
  label: string;
  value: string;
}

interface FormFieldProps {
  id: string;
  label: string;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  value: any;
  touched: boolean | undefined;
  error: string | undefined;
  onChange?: (e: React.ChangeEvent<any>) => void;
  onBlur: (e: React.FocusEvent<any>) => void;
  options?: Option[]; // For select fields
  onSelectChange?: (option: Option | null) => void; // For react-select
  placeholder?: string;
  step?: string;
  // For search-select (debounced autocomplete)
  onSearchInput?: (searchTerm: string) => void;
  isSearching?: boolean;
  debounceDelay?: number;
  defaultValue?: any;
}

const FormField: React.FC<FormFieldProps> = ({
  id,
  label,
  type = 'text',
  required,
  disabled,
  value,
  touched,
  error,
  onChange,
  onBlur,
  options,
  onSelectChange,
  placeholder,
  step,
  onSearchInput,
  isSearching = false,
  debounceDelay = 300,
  defaultValue,
}) => {
  const [searchInput, setSearchInput] = useState<string>('');
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const hasSelectedRef = useRef<boolean>(false);

  // Debounce the search input and notify parent
  const debouncedSearchTerm = useDebounce(searchInput, debounceDelay);

  // Clear search only when selection is made and field loses focus
  useEffect(() => {
    if (type === 'search-select' && onSearchInput && isFocused) {
      onSearchInput(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, type, onSearchInput, isFocused]);

  // Handle search input change
  const handleSearchInputChange = (inputValue: string) => {
    setSearchInput(inputValue);
  };

  // Handle selection
  const handleSelectChange = (option: Option | null) => {
    hasSelectedRef.current = true;
    onSelectChange?.(option);
  };

  // Handle blur - clear search and optionally notify parent
  const handleBlur = () => {
    setIsFocused(false);
    // Clear search input after selection is made
    if (hasSelectedRef.current) {
      setSearchInput('');
      hasSelectedRef.current = false;
      // Optional: Notify parent to clear search results
      if (onSearchInput) {
        onSearchInput('');
      }
    }
    onBlur({ target: { name: id } } as any);
  };

  // Handle focus
  const handleFocus = () => {
    setIsFocused(true);
  };

  // Find the selected option
  const selectedOption = defaultValue || options?.find((opt) => opt.value === value) || null;

  return (
    <FormGroup check={type === 'checkbox'}>
      {type === 'checkbox' ? (
        <div className="form-check mt-2 mb-2">
          <Input
            id={id}
            name={id}
            type="checkbox"
            className="form-check-input"
            checked={value}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
          />
          <Label for={id} className="form-check-label" style={{ marginLeft: '1rem' }}>
            {label} {required && <span className="text-danger">*</span>}
          </Label>
        </div>
      ) : type === 'search-select' ? (
        <div className="mb-2 mt-2">
          <Label for={id}>
            {label} {required && <span className="text-danger">*</span>}
          </Label>
          <div style={{ position: 'relative' }}>
            <Select
              id={id}
              name={id}
              options={options}
              value={selectedOption}
              onChange={handleSelectChange}
              onInputChange={handleSearchInputChange}
              onBlur={handleBlur}
              onFocus={handleFocus}
              isDisabled={disabled}
              isLoading={isSearching}
              isClearable
              isSearchable
              placeholder={placeholder || `Search and select ${label}`}
              noOptionsMessage={() => (isSearching ? 'Loading options...' : 'No options found')}
              // Keep the menu open after selection to avoid flicker
              closeMenuOnSelect={true}
              // Don't blur on selection
              blurInputOnSelect={false}
            />
          </div>
        </div>
      ) : type === 'select' ? (
        <div className="mb-2 mt-2">
          <Label for={id}>
            {label} {required && <span className="text-danger">*</span>}
          </Label>
          <Select
            id={id}
            name={id}
            options={options}
            value={selectedOption}
            onChange={onSelectChange}
            onBlur={() => onBlur({ target: { name: id } } as any)}
            isDisabled={disabled}
            placeholder={placeholder || `Select ${label}`}
          />
        </div>
      ) : (
        <div className="mb-2 mt-2">
          <Label for={id}>
            {label} {required && <span className="text-danger">*</span>}
          </Label>
          <Input
            id={id}
            name={id}
            type={type as InputType}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
            invalid={!!(touched && error)}
            placeholder={placeholder}
            step={step}
          />
        </div>
      )}
      {touched && error && <div className="text-danger mt-1">{error}</div>}
    </FormGroup>
  );
};

export default FormField;
